import { supabase } from '../lib/supabase';

const CATEGORY_DISPLAY_NAMES = {
  WEIGHT_LOSS_TIPS: 'Weight Loss Tips',
  HEALTHY_RECIPES: 'Healthy Recipes',
  NUTRITION: 'Nutrition',
  EXERCISE: 'Exercise',
  GLP1_MEDICATION: 'GLP-1 Medication',
  MENTAL_HEALTH: 'Mental Health',
  SUCCESS_STORIES: 'Success Stories',
  LIFESTYLE: 'Lifestyle',
  SLEEP_WELLNESS: 'Sleep & Wellness',
  METABOLISM: 'Metabolism',
};

// Map Supabase snake_case columns to the camelCase shape the UI expects
const mapBlog = (b) => ({
  ...b,
  featuredImage: b.featured_image,
  readTime: b.read_time,
  viewCount: b.view_count,
  createdAt: b.created_at,
  updatedAt: b.updated_at,
  publishedAt: b.published_at,
  authorName: b.author_name,
  authorTitle: b.author_title,
  authorImage: b.author_image,
  isPublished: b.is_published,
  isFeatured: b.is_featured,
  createdBy: b.created_by,
  categoryDisplayName: CATEGORY_DISPLAY_NAMES[b.category] || b.category,
});

export const blogAPI = {
  getAllBlogs: async () => {
    const { data, error } = await supabase
      .from('blogs')
      .select('*')
      .eq('is_published', true)
      .order('published_at', { ascending: false });
    if (error) throw error;
    return { data: (data ?? []).map(mapBlog) };
  },

  getBlogsWithPagination: async (page = 0, size = 10, category = null) => {
    let query = supabase
      .from('blogs')
      .select('*', { count: 'exact' })
      .eq('is_published', true)
      .order('published_at', { ascending: false })
      .range(page * size, page * size + size - 1);
    if (category) query = query.eq('category', category);
    const { data, error, count } = await query;
    if (error) throw error;
    return { data: { content: (data ?? []).map(mapBlog), totalElements: count, totalPages: Math.ceil((count ?? 0) / size) } };
  },

  getBlogById: async (id) => {
    const { data, error } = await supabase
      .from('blogs').select('*').eq('id', id).single();
    if (error) throw error;
    supabase.from('blogs').update({ view_count: (data.view_count ?? 0) + 1 }).eq('id', id).then(() => {});
    return { data: mapBlog(data) };
  },

  getBlogBySlug: async (slug) => {
    const { data, error } = await supabase
      .from('blogs').select('*').eq('slug', slug).eq('is_published', true).single();
    if (error) throw error;
    supabase.from('blogs').update({ view_count: (data.view_count ?? 0) + 1 }).eq('id', data.id).then(() => {});
    return { data: mapBlog(data) };
  },

  getBlogsByCategory: async (category) => {
    const { data, error } = await supabase
      .from('blogs').select('*').eq('category', category).eq('is_published', true)
      .order('published_at', { ascending: false });
    if (error) throw error;
    return { data: (data ?? []).map(mapBlog) };
  },

  getFeaturedBlogs: async () => {
    const { data, error } = await supabase
      .from('blogs').select('*').eq('is_featured', true).eq('is_published', true)
      .order('published_at', { ascending: false });
    if (error) throw error;
    return { data: (data ?? []).map(mapBlog) };
  },

  searchBlogs: async (keyword) => {
    const { data, error } = await supabase
      .from('blogs').select('*')
      .eq('is_published', true)
      .or(`title.ilike.%${keyword}%,summary.ilike.%${keyword}%,content.ilike.%${keyword}%`)
      .order('published_at', { ascending: false });
    if (error) throw error;
    return { data: (data ?? []).map(mapBlog) };
  },

  getRelatedBlogs: async (id, category) => {
    const { data, error } = await supabase
      .from('blogs').select('*')
      .eq('category', category).eq('is_published', true)
      .neq('id', id).limit(3)
      .order('published_at', { ascending: false });
    if (error) throw error;
    return { data: (data ?? []).map(mapBlog) };
  },

  getCategories: async () => {
    const categories = [
      'WEIGHT_LOSS_TIPS','HEALTHY_RECIPES','NUTRITION','EXERCISE',
      'GLP1_MEDICATION','MENTAL_HEALTH','SUCCESS_STORIES',
      'LIFESTYLE','SLEEP_WELLNESS','METABOLISM',
    ];
    return { data: categories };
  },

  getCategoryCounts: async () => {
    const { data, error } = await supabase
      .from('blogs').select('category').eq('is_published', true);
    if (error) throw error;
    const counts = (data ?? []).reduce((acc, row) => {
      acc[row.category] = (acc[row.category] ?? 0) + 1;
      return acc;
    }, {});
    return { data: counts };
  },

  createBlog: async (blogData) => {
    const { data: { user } } = await supabase.auth.getUser();
    // Convert camelCase input from AddBlog.js to snake_case for Supabase
    const row = {
      title: blogData.title,
      slug: blogData.slug || blogData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      summary: blogData.summary,
      content: blogData.content,
      category: blogData.category,
      featured_image: blogData.featuredImage || blogData.featured_image || null,
      author_name: blogData.authorName || blogData.author_name || null,
      author_title: blogData.authorTitle || blogData.author_title || null,
      author_image: blogData.authorImage || blogData.author_image || null,
      read_time: blogData.readTime || blogData.read_time || null,
      is_published: blogData.isPublished ?? blogData.is_published ?? true,
      is_featured: blogData.isFeatured ?? blogData.is_featured ?? false,
      created_by: user?.id,
      published_at: (blogData.isPublished ?? blogData.is_published) ? new Date().toISOString() : null,
    };
    const { data, error } = await supabase.from('blogs').insert(row).select().single();
    if (error) throw error;
    return { data: mapBlog(data) };
  },

  updateBlog: async (id, blogData) => {
    // Convert camelCase input to snake_case
    const updates = {
      title: blogData.title,
      summary: blogData.summary,
      content: blogData.content,
      category: blogData.category,
      featured_image: blogData.featuredImage || blogData.featured_image || null,
      author_name: blogData.authorName || blogData.author_name || null,
      author_title: blogData.authorTitle || blogData.author_title || null,
      author_image: blogData.authorImage || blogData.author_image || null,
      read_time: blogData.readTime || blogData.read_time || null,
      is_published: blogData.isPublished ?? blogData.is_published,
      is_featured: blogData.isFeatured ?? blogData.is_featured,
    };
    // Remove undefined fields so we don't overwrite with null accidentally
    Object.keys(updates).forEach(k => updates[k] === undefined && delete updates[k]);
    const { data, error } = await supabase.from('blogs').update(updates).eq('id', id).select().single();
    if (error) throw error;
    return { data: mapBlog(data) };
  },

  deleteBlog: async (id) => {
    const { error } = await supabase.from('blogs').delete().eq('id', id);
    if (error) throw error;
    return { data: { message: 'Blog deleted' } };
  },
};

export default blogAPI;
